import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Grid,
  AppBar,
  Toolbar,
  Typography,
  Card,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Drawer,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Home as HomeIcon,
  Inventory as InventoryIcon,
  Build as BuildIcon,
  Dashboard as DashboardIcon,
  Mail as MailIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { FaBars, FaBell, FaEnvelope, FaCog, FaUser } from 'react-icons/fa';
import logo from '../../assets/logo.png'; // Ajuste o caminho conforme necessário
import imagewall from '../../assets/imagewall.png';

// Largura fixa da sidebar
const drawerWidth = 240;

// Card personalizado para a sidebar com transparência e cantos arredondados
const SidebarCard = styled(Card)(({ theme }) => ({
  width: drawerWidth,
  height: '100vh',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  boxShadow: 'none',
}));

// Card principal para o conteúdo, com transparência e cantos arredondados
const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  boxShadow: 'none',
}));

function Layout({ children }) {
  const theme = useTheme();
  const navigate = useNavigate();

  // State hooks for drawer and menus
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [defAnchorEl, setDefAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [submenuKey, setSubmenuKey] = useState('');

  // Handler for toggling the drawer
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Example data for demonstration. Replace with actual logic or props/context as needed.
  const user = { nome_completo: 'Usuário Exemplo' }; // Replace with actual user
  const notifications = []; // Replace with actual notifications array
  const unreadNotificationsCount = notifications ? notifications.filter(n => !n.is_read).length : 0;
  const handleLogout = () => { /* Implement logout logic */ };
  const handleNotificationClick = (notification) => { /* Implement notification click logic */ };

  // Drawer content, adapt as needed
  const drawer = (
    <Box>
      <Toolbar />
      <Divider sx={{ mb: 2 }} />
      <List>
        <ListItemButton
          onClick={() => navigate('/home')}
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(173, 216, 230, 0.3)',
            },
          }}
        >
          <ListItemIcon>
            <HomeIcon sx={{ color: '#ADD8E6' }} />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate('/stocks')}
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(173, 216, 230, 0.3)',
            },
          }}
        >
          <ListItemIcon>
            <InventoryIcon sx={{ color: '#ADD8E6' }} />
          </ListItemIcon>
          <ListItemText primary="Stocks" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate('/cliches-moldes')}
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(173, 216, 230, 0.3)',
            },
          }}
        >
          <ListItemIcon>
            <BuildIcon sx={{ color: '#ADD8E6' }} />
          </ListItemIcon>
          <ListItemText primary="Clichês" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate('/op')}
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(173, 216, 230, 0.3)',
            },
          }}
        >
          <ListItemIcon>
            <DashboardIcon sx={{ color: '#ADD8E6' }} />
          </ListItemIcon>
          <ListItemText primary="Operacional" />
        </ListItemButton>
      </List>
    </Box>
  );

  // Handlers for user menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handlers for notification menu
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
      }}
    >
      {/* AppBar superior com logo e botões de usuário */}
      <Card
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          {/* Ícone para abrir/fechar o drawer */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: 'none' }, fontSize: { xs: 24, sm: 32 } }}
          >
            <FaBars style={{ color: '#ADD8E6' }} />
          </IconButton>

          {/* Logo e título */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="img" src={logo} alt="Logo" sx={{ height: '32px', mr: 1 }} />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontSize: { xs: '1rem', sm: '1.5rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Portal do Trabalhador
            </Typography>
          </Box>

          {/* Botões de notificações, e-mail, definições e usuário */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" onClick={handleNotificationMenuOpen} sx={{ mr: 1 }}>
              <Badge badgeContent={unreadNotificationsCount} color="error">
                <FaBell style={{ fontSize: 24, color: '#ADD8E6' }} />
              </Badge>
            </IconButton>
            <IconButton color="inherit" onClick={() => navigate('/emails')} sx={{ mr: 1 }}>
              <FaEnvelope style={{ fontSize: 24, color: '#ADD8E6' }} />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={(event) => setDefAnchorEl(event.currentTarget)}
              sx={{ mr: 1 }}
            >
              <FaCog style={{ fontSize: 24, color: '#ADD8E6' }} />
              {!isMobile && (
                <Typography sx={{ ml: 0.5, display: { xs: 'none', sm: 'block' }, fontSize: '1rem' }}>
                  Definições
                </Typography>
              )}
            </IconButton>
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <FaUser style={{ fontSize: 24, color: '#ADD8E6' }} />
              <Typography variant="body1" sx={{ ml: 1, fontSize: '1rem' }}>
                Bem vindo, {user.nome_completo.split(' ')[0]}
              </Typography>
            </IconButton>

            {/* Menu de usuário */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  width: '180px',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: 2,
                  boxShadow: 'none',
                },
              }}
            >
              <MenuItem onClick={() => { handleMenuClose(); navigate('/account-settings', { state: { user } }); }}>
                Definições de conta
              </MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); navigate('/marcar-ferias', { state: { user } }); }}>
                Marcar Férias
              </MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); navigate('/faltas'); }}>Faltas</MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>Terminar Sessão</MenuItem>
            </Menu>

            {/* Menu de notificações */}
            <Menu
              anchorEl={notificationAnchorEl}
              open={Boolean(notificationAnchorEl)}
              onClose={handleNotificationMenuClose}
              PaperProps={{
                sx: {
                  width: '280px',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: 2,
                  boxShadow: 'none',
                },
              }}
            >
              {(!notifications || notifications.length === 0) ? (
                <MenuItem onClick={handleNotificationMenuClose}>Nenhuma nova notificação</MenuItem>
              ) : (
                notifications.map((notification, index) => (
                  <Tooltip key={index} title={notification.message} arrow>
                    <MenuItem
                      onClick={() => { handleNotificationMenuClose(); handleNotificationClick(notification); }}
                      sx={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        backgroundColor: notification.is_read ? 'white' : '#f0f0f0',
                        fontWeight: notification.is_read ? 'normal' : 'bold'
                      }}
                    >
                      {notification.message}
                    </MenuItem>
                  </Tooltip>
                ))
              )}
            </Menu>

            <Menu
              anchorEl={defAnchorEl}
              open={Boolean(defAnchorEl)}
              onClose={() => setDefAnchorEl(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: 2,
                  boxShadow: 'none',
                  mt: 1
                },
              }}
            >
              {['artigo_phc', 'tipo_saco', 'tamanho', 'tipo_impressao'].map((key) => (
                <MenuItem
                  key={key}
                  onClick={(event) => {
                    setSubmenuAnchorEl(event.currentTarget);
                    setSubmenuKey(key);
                  }}
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {key.replace('_', ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Menu>

            <Menu
              anchorEl={submenuAnchorEl}
              open={Boolean(submenuAnchorEl)}
              onClose={() => setSubmenuAnchorEl(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: 2,
                  boxShadow: 'none',
                  mt: 0,
                },
              }}
            >
              {submenuKey === 'artigo_phc' ? (
                ['Importar', 'Consultar'].map((action) => (
                  <MenuItem key={action} onClick={() => {
                    setSubmenuAnchorEl(null);
                    setDefAnchorEl(null);
                    navigate(`/config/artigo_phc?mode=${action.toLowerCase()}`);
                  }}>
                    {action}
                  </MenuItem>
                ))
              ) : (
                ['Criar', 'Consultar'].map((action) => (
                  <MenuItem key={action} onClick={() => {
                    setSubmenuAnchorEl(null);
                    setDefAnchorEl(null);
                    navigate(`/config/${submenuKey}?mode=${action.toLowerCase()}`);
                  }}>
                    {action}
                  </MenuItem>
                ))
              )}
            </Menu>
          </Box>
        </Toolbar>
      </Card>

      {/* Drawer esquerdo */}
      <Box component="nav" sx={{ width: { sm: drawerOpen ? 250 : 60 }, flexShrink: { sm: 0 } }} aria-label="mailbox folders">
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 250,
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
        >
          <SidebarCard>
            {drawer}
          </SidebarCard>
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerOpen ? 250 : 60,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: 'width 0.3s ease',
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
          open={drawerOpen}
        >
          <SidebarCard>
            {drawer}
          </SidebarCard>
        </Drawer>
      </Box>

      {/* Conteúdo principal */}
      <Grid
        container
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          transition: 'margin 0.3s ease',
          marginLeft: { sm: drawerOpen ? '20px' : '50px', xs: '0px' },
          mt: { xs: '64px', sm: '64px' }
        }}
      >
        <Toolbar />
        <ContentCard>
          {children}
        </ContentCard>
      </Grid>
    </Box>
  );
}

export default Layout;